import { formatDate } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import {
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { getFormattedDate, isLargeScreen } from '../../../../global.variable';
import { trashIconIf } from '../../../../roster-common-functions';
import { AppService } from '../../../app.service';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { ModalDialogueService } from '../../../shared/components/modal-dialogue/modal-dialogue.service';
import { SchedulerComponent } from '../../../shared/components/scheduler/scheduler.component';
import { DataCheckService } from '../../../shared/services/data-check.service';
import { DeviceInfoService } from '../../../shared/services/device-info.service';
import { LoadingSpinnerService } from '../../../shared/services/loading-spinner.service';
import { ToasterService } from '../../../shared/services/toaster.service';
import { WebSocketService } from '../../../shared/services/web-socket.service';
import { CheckpointService } from '../checkpoints/checkpoint.service';
import { ClientsService } from '../clients/clients.service';
import { JobsService } from '../jobs/jobs.service';
import { RosterShiftsService } from './roster-shifts.service';

@Component({
  selector: 'gtapp-roster-shifts',
  templateUrl: './roster-shifts.component.html',
  styleUrl: './roster-shifts.component.scss',
  standalone: false,
})
export class RosterShiftsComponent implements OnInit, OnDestroy {
  isAdmin = this.dataCheckService.isUserAdmin();
  isDispatchUser = this.dataCheckService.isDispatchUser();

  largeView: Boolean = isLargeScreen;

  isLargeScreenSubscription: Subscription = new Subscription();
  webSocketSubscription: Subscription = new Subscription();
  navigationEndSubscription: Subscription = new Subscription();

  loggedInDeviceID: string = this.deviceInfoService.getUniqueDeviceID();

  weatherData: any = {};
  weatherAddressInfo: any = {};

  shiftWeekDays: any = [];
  selectedSearchDate: any;
  isCurrentWeek = false;

  groupedJobRosterShiftData: any = {};
  totalJobCount: number = 0;
  pageSizeJobs: number = 30;
  previousJobs: number = 0;
  pulledJobCount: number = 0;
  jobFinalApiCall: boolean = false;

  groupedPatrolJobRosterShiftData: any = {};
  totalPatrolJobCount: number = 0;
  pageSizePatrolJobs: number = 30;
  previousPatrolJobs: number = 0;
  pulledPatrolJobCount: number = 0;
  patrolJobFinalApiCall: boolean = false;

  patrolMissedDataList: any = [];
  patrolCheckPointCountDictData: any = {};

  fullGroupedRosterData: any = {};
  fullGroupedRosterBackupData: any = {};
  groupCountsByDate: any = {};

  shiftDataGroupBy: string = 'company';
  jobCardStyling: any = { status: {}, groupStyle: {} };

  jobStatusDataList: any = [];
  jobTypesDataList: any = [];

  conflictData: any = {};

  shiftRecurranceSelection: number = 1;

  // mobile scroll logic variables
  isAnimating = false;
  startX: number = 0;

  // assignee selection variables
  assigneeListData: any = [];
  selectedAssignee: any = {};

  // filter job params
  clientDropDownList: any = [];
  siteDropDownList: any = [];
  assigneeDropDownList: any = [];
  initialDataList: any = {};

  appliedFilters: any = {};

  shiftsToPublish: any = [];
  // second variable is to get filtered shifts ie exclude shifts which have been published before
  shiftsToPublishListFiltered: any = [];
  declineReason: string = '';

  subscriberUserId =
    this.appService.getUserData()?.subscriber?.subscriber_user_id;

  nextSetJobSubject = new Subject<void>();
  nextSetPatrolJobSubject = new Subject<void>();
  jobSubject = new Subject<void>();
  patrolJobSubject = new Subject<void>();

  groupByIds: any = [];

  publishedHistoryData: any;

  groupedDataList: any = [];

  expandAllGroups: boolean = true;
  selectedGroupInfo: any;
  groupedCountHack: number = 0;

  shiftStatus: any;

  constructor(
    private spinnerService: LoadingSpinnerService,
    private jobService: JobsService,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: ModalDialogueService,
    private appService: AppService,
    private dataCheckService: DataCheckService,
    private rsService: RosterShiftsService,
    private viewContainerRef: ViewContainerRef,
    private toasterService: ToasterService,
    private deviceInfoService: DeviceInfoService,
    private clientService: ClientsService,
    private checkPointService: CheckpointService,
  ) {}
  ngOnDestroy(): void {
    window.onpopstate = null;

    this.isLargeScreenSubscription?.unsubscribe();
    if (this.webSocketSubscription) {
      this.webSocketSubscription?.unsubscribe();
    }
    this.nextSetJobSubject.next();
    this.nextSetJobSubject.complete();

    this.nextSetPatrolJobSubject.next();
    this.nextSetPatrolJobSubject.complete();

    this.jobSubject.next();
    this.jobSubject.complete();

    this.patrolJobSubject.next();
    this.patrolJobSubject.complete();
  }
  stopAllSubjects() {
    this.nextSetJobSubject.next();
    this.nextSetPatrolJobSubject.next();
    this.jobSubject.next();
    this.patrolJobSubject.next();
  }

  ngOnInit(): void {
    this.getJobStatuses();
    this.getWeatherInfo();
    if (this.isAdmin || this.isDispatchUser) {
      this.getShiftStats();
    }

    if (!this.loggedInDeviceID) {
      this.loggedInDeviceID = this.deviceInfoService.getUniqueDeviceID();
    }
    if (this.route.snapshot.fragment) {
      const day = new Date(this.route.snapshot.fragment);
      this.getWeekStartDay(day);
    } else {
      this.getWeekStartDay();
    }
  }
  getShiftStats() {
    let param: any = {
      time_offset: new Date().getTimezoneOffset(),
      job_stats: 1,
    };

    this.jobService.getDashBoardData(param).subscribe((response: any) => {
      if (response['status'] == 'success') {
        this.shiftStatus = response?.data?.job_stats;
      }
    });
  }
  getJobStatuses() {
    this.jobService.getJobParams().subscribe((response: any) => {
      if (response['status'] == 'success') {
        this.jobStatusDataList = response.statuses;
        // get only job types with roster
        this.jobTypesDataList = response.data?.filter((jType: any) =>
          [1, 2, 3, 4].includes(jType?.type_identity),
        );

        this.jobStatusDataList.push({
          id: '0',
          name: 'Closed',
          additional_info: {
            style: {
              snubbed: {
                'color': 'var(--color-basic-1100)',
                'background-color': 'var(--color-basic-100)',
                'border': '1px solid var(--color-basic-1100)',
                'border-radius': '5px',
                'font-size': 'small',
                'padding-left': '2rem',
                'padding-right': '2rem',
                'text-align': 'center',
              },
            },
          },
        });
      }

      this.jobStatusDataList?.forEach((element: any) => {
        this.jobCardStyling['status'][element?.name] = {
          'color': `${element.additional_info?.style?.snubbed?.color}`,
          'background-color': `${element.additional_info?.style?.snubbed['background-color']}`,
          'border': `1px solid ${element.additional_info?.style?.snubbed['background-color']}`,
          'border-radius': '5px',
          'font-size': 'small',
          'padding-left': '2rem',
          'padding-right': '2rem',
          'text-align': 'center',
        };

        this.jobCardStyling['groupStyle'][element?.name] = {
          'border-color': `${element.additional_info?.style?.snubbed['border-color']}`,
        };
      });

      this.jobCardStyling['status']['Not Started'] = {
        'color': 'var(--bs-white)', // Bootstrap white text
        'background-color': 'var(--bs-warning)', // Bootstrap warning background
        'border': '1px solid var(--bs-warning)',
        'border-radius': '5px',
        'font-size': 'small',
        'padding-left': '2rem',
        'padding-right': '2rem',
        'text-align': 'center',
      };
      this.jobCardStyling['status']['In Progress'] = {
        'color': 'var(--bs-white)', // Bootstrap white text
        'background-color': 'var(--bs-success)', // Bootstrap success background
        'border': '1px solid var(--bs-success)',
        'border-radius': '5px',
        'font-size': 'small',
        'padding-left': '2rem',
        'padding-right': '2rem',
        'text-align': 'center',
      };
      this.jobCardStyling['status']['Late to Shift'] = {
        'color': 'var(--bs-white)', // Bootstrap white text
        'background-color': 'var(--bs-danger)', // Bootstrap danger background
        'border': '1px solid var(--bs-danger)',
        'border-radius': '5px',
        'font-size': 'small',
        'padding-left': '2rem',
        'padding-right': '2rem',
        'text-align': 'center',
      };
      this.jobCardStyling['status']['Unassigned Shift'] = {
        'color': 'var(--bs-danger)', // Bootstrap white text
        'background-color': 'var(--bs-light)', // Bootstrap primary background
        'border': '1px solid var(--bs-light)',
        'border-radius': '5px',
        'font-size': 'small',
        'padding-left': '2rem',
        'padding-right': '2rem',
        'text-align': 'center',
      };
      this.jobCardStyling['status']['Shift Complete'] = {
        'color': 'var(--bs-white)', // Bootstrap white text
        'background-color': 'var(--bs-dark)', // Bootstrap dark background
        'border': '1px solid var(--bs-dark)',
        'border-radius': '5px',
        'font-size': 'small',
        'padding-left': '2rem',
        'padding-right': '2rem',
        'text-align': 'center',
      };
      // make sure status names are in sync with roster-common-functions `getJobStatus` method
      this.jobCardStyling['status']['Stale'] = {
        'color': 'var(--bs-white)', // Bootstrap white text
        'background-color': 'var(--bs-dark)', // Bootstrap dark background
        'border': '1px solid var(--bs-dark)',
        'border-radius': '5px',
        'font-size': 'small',
        'padding-left': '2rem',
        'padding-right': '2rem',
        'text-align': 'center',
      };
      // make sure status names are in sync with roster-common-functions `getJobStatus` method
      this.jobCardStyling['status']['Complete'] = {
        'color': 'var(--bs-white)', // Bootstrap white text
        'background-color': 'var(--bs-dark)', // Bootstrap dark background
        'border': '1px solid var(--bs-dark)',
        'border-radius': '5px',
        'font-size': 'small',
        'padding-left': '2rem',
        'padding-right': '2rem',
        'text-align': 'center',
      };
      // make sure status names are in sync with roster-common-functions `getJobStatus` method
      this.jobCardStyling['status']['Complete with Missed Scans'] = {
        'color': 'var(--bs-white)', // Bootstrap white text
        'background-color': 'var(--bs-dark)', // Bootstrap dark background
        'border': '1px solid var(--bs-dark)',
        'border-radius': '5px',
        'font-size': 'small',
        'padding-left': '2rem',
        'padding-right': '2rem',
        'text-align': 'center',
      };
    });
  }
  updatePreferences(weekLength: number) {
    let userData = this.appService.getUserData();
    if (userData?.preferences) {
      userData.preferences.rosterWeekLength = weekLength;
      this.appService.setUserData(userData);
    }
  }

  getWeekStartDay(date: Date = new Date()) {
    const currentDay = new Date(date);
    this.generateWeekDays(currentDay);
  }
  getOnlyDateFromDateTime(date?: any) {
    const updateDate = date ? new Date(date) : new Date();
    updateDate.setHours(0, 0, 0, 0);
    return updateDate;
  }

  generateWeekDays(date: Date) {
    const days: Date[] = [];
    const currentDayOftheWeek = this.getOnlyDateFromDateTime(date);
    this.selectedSearchDate = getFormattedDate(currentDayOftheWeek);

    const today = this.getOnlyDateFromDateTime();

    this.isCurrentWeek = false;

    for (let i = 0; i < 7; i++) {
      const nextDay = new Date(currentDayOftheWeek);
      nextDay.setDate(currentDayOftheWeek.getDate() + i);

      if (!this.isCurrentWeek) {
        this.isCurrentWeek = today.getTime() === nextDay.getTime();
      }

      days.push(nextDay);
    }

    this.shiftWeekDays = days;

    this.getRosterShifts();
  }
  async onDateSelect(type: 'week' | 'day' | 'search', action: '+' | '-' | '=') {
    if (type == 'week') {
      if (action == '=') {
        this.getWeekStartDay();
      } else {
        const newDate = new Date(this.selectedSearchDate);
        newDate.setDate(newDate.getDate() + (action === '+' ? 7 : -7));
        this.generateWeekDays(newDate);
      }
    } else if (type == 'day') {
      if (action == '=') {
        const selectedDate = new Date();
        this.generateWeekDays(selectedDate);
      } else {
        const newDate = new Date(this.selectedSearchDate);
        newDate.setDate(newDate.getDate() + (action === '+' ? 1 : -1));
        this.generateWeekDays(newDate);
      }
    } else {
      if (action == '=') {
        const selectedDate = this.selectedSearchDate
          ? new Date(this.selectedSearchDate)
          : new Date();
        this.getWeekStartDay(selectedDate);
      }
    }
    this.router.navigate(['/roster'], {
      fragment: this.selectedSearchDate,
      queryParams: {},
      replaceUrl: true,
    });
  }

  formatDateWithoutTime = (date: any) => {
    date = new Date(date);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  };

  getRosterShifts() {
    this.shiftDataGroupBy = atob(
      localStorage.getItem(btoa('rosterGroupby')) ||
        btoa(this.shiftDataGroupBy),
    );

    if (this.largeView)
      this.expandAllGroups = Boolean(
        atob(localStorage.getItem(btoa('expandAllGroups')) || btoa('false')) ==
        'true',
      );

    this.stopAllSubjects();
    this.getRosterStaticJobs();
    this.getRosterPatrolJobs();
  }
  getStartEndDays() {
    const startDay = new Date(this.selectedSearchDate);
    startDay.setHours(0, 0, 0, 0);
    const endDay = new Date(startDay);
    endDay.setDate(endDay.getDate() + 6);
    return {
      start_day: formatDate(startDay, 'yyyy-MM-dd', 'en_US'),
      end_day: formatDate(endDay, 'yyyy-MM-dd', 'en_US'),
    };
  }
  getNextSetOfJobs() {
    const groupBy = !this.expandAllGroups ? this.shiftDataGroupBy : null;
    let reqParams: any = {
      ...this.getStartEndDays(),
      jobs: 1,
      previous: this.pulledJobCount,
      rows: this.pageSizeJobs + 10,
      group_by: groupBy,
    };
    if (
      Object.keys(this.appliedFilters || {})?.length &&
      !this.expandAllGroups
    ) {
      reqParams.search_filters = JSON.stringify(this.appliedFilters || {});
    }
    if (this.selectedGroupInfo?.grouped_id)
      reqParams.grouped_id = this.selectedGroupInfo?.grouped_id;
    this.rsService
      .getRosterShifts(reqParams)
      .pipe(takeUntil(this.nextSetJobSubject))
      .subscribe((response: any) => {
        if (response?.status === 'success') {
          this.totalJobCount = response?.total_size;
          this.formatScheduleData('job', response?.data);
        } else {
          this.toasterService.setMessage({
            errorMessage: response?.message,
            successMessage: '',
          });
        }
      });
  }
  getNextSetOfPatrolJobs() {
    const groupBy = !this.expandAllGroups ? this.shiftDataGroupBy : null;
    let reqParams: any = {
      ...this.getStartEndDays(),
      previous: this.pulledPatrolJobCount,
      rows: this.pageSizePatrolJobs + 10,
      group_by: groupBy,
    };
    if (
      Object.keys(this.appliedFilters || {})?.length &&
      !this.expandAllGroups
    ) {
      reqParams.search_filters = JSON.stringify(this.appliedFilters || {});
    }
    if (this.selectedGroupInfo?.grouped_id)
      reqParams.grouped_id = this.selectedGroupInfo?.grouped_id;
    this.rsService
      .getRosterShifts(reqParams)
      .pipe(takeUntil(this.nextSetPatrolJobSubject))
      .subscribe((response: any) => {
        if (response?.status === 'success') {
          this.totalJobCount = response?.total_size;
          this.patrolCheckPointCountDictData = {
            ...this.patrolCheckPointCountDictData,
            ...response?.cp_count_data,
          };
          this.formatScheduleData('patrol', response?.data);
        } else {
          this.toasterService.setMessage({
            errorMessage: response?.message,
            successMessage: '',
          });
        }
      });
  }
  getRosterStaticJobs() {
    this.jobFinalApiCall = false;

    const groupBy = !this.expandAllGroups ? this.shiftDataGroupBy : null;
    let reqParams: any = {
      ...this.getStartEndDays(),
      jobs: 1,
      previous: 0,
      rows: this.pageSizeJobs,
      group_by: groupBy,
    };
    if (
      Object.keys(this.appliedFilters || {})?.length &&
      !this.expandAllGroups
    ) {
      reqParams.search_filters = JSON.stringify(this.appliedFilters || {});
    }
    if (this.selectedGroupInfo?.grouped_id)
      reqParams.grouped_id = this.selectedGroupInfo?.grouped_id;
    this.spinnerService.show('Getting Job Shifts');
    this.rsService
      .getRosterShifts(reqParams)
      .pipe(takeUntil(this.jobSubject))
      .subscribe((response: any) => {
        if (response?.status === 'success') {
          if (groupBy && !this.selectedGroupInfo?.grouped_id) {
            this.updateGroupedDataList(response?.data);
            this.spinnerService.hide();
          } else {
            this.totalJobCount = response?.total_size;
            this.formatScheduleData('job', response?.data, true);
          }
        } else {
          this.toasterService.setMessage({
            errorMessage: response?.message,
            successMessage: '',
          });
        }
      });
  }
  updateGroupedDataList(responseData: any) {
    this.groupedCountHack += 1;
    const merged = [...this.groupedDataList, ...(responseData || [])].reduce(
      (acc, item) => {
        acc[item.grouped_id] = item;
        return acc;
      },
      {},
    );

    // Convert back to array
    let groupedDataList = Object.values(merged);
    groupedDataList.sort((a: any, b: any) => {
      if (this.shiftDataGroupBy == 'time') {
        const hourA = parseInt(a?.grouped_name.split(':')[0], 10);
        const hourB = parseInt(b?.grouped_name.split(':')[0], 10);
        return hourA - hourB;
      } else {
        return a?.grouped_name.localeCompare(b?.grouped_name);
      }
    });

    const itemInLast = groupedDataList?.findIndex((item: any) =>
      [4, '4', 'unassigned'].includes(item?.grouped_id),
    );

    if (itemInLast !== -1) {
      const [item] = groupedDataList.splice(itemInLast, 1);
      groupedDataList.push(item);
    }

    this.groupedDataList = [...groupedDataList];

    if (this.groupedCountHack % 2 == 0 && this.groupedDataList?.length) {
      // both apis will be called now
      const selectedGroupInfo = JSON.parse(
        sessionStorage.getItem(btoa('selectedGroupInfo')) || '{}',
      );

      const groupInfoInList = this.groupedDataList?.find(
        (item: any) => item?.grouped_id == selectedGroupInfo?.grouped_id,
      );

      if (groupInfoInList?.grouped_id) {
        this.getGroupedDataJobList(groupInfoInList);
      } else {
        this.getGroupedDataJobList(this.groupedDataList[0]);
      }
    }
  }
  getRosterPatrolJobs() {
    this.patrolJobFinalApiCall = false;

    const groupBy = !this.expandAllGroups ? this.shiftDataGroupBy : null;
    let reqParams: any = {
      ...this.getStartEndDays(),
      previous: 0,
      rows: this.pageSizePatrolJobs,
      group_by: groupBy,
    };
    if (
      Object.keys(this.appliedFilters || {})?.length &&
      !this.expandAllGroups
    ) {
      reqParams.search_filters = JSON.stringify(this.appliedFilters || {});
    }
    if (this.selectedGroupInfo?.grouped_id)
      reqParams.grouped_id = this.selectedGroupInfo?.grouped_id;
    this.spinnerService.show('Getting Checkpoint Patrol Shifts');
    this.rsService
      .getRosterShifts(reqParams)
      .pipe(takeUntil(this.patrolJobSubject))
      .subscribe((response: any) => {
        if (response?.status === 'success') {
          if (groupBy && !this.selectedGroupInfo?.grouped_id) {
            this.updateGroupedDataList(response?.data);
            this.spinnerService.hide();
          } else {
            this.totalPatrolJobCount = response?.total_size;
            this.patrolCheckPointCountDictData = {
              ...this.patrolCheckPointCountDictData,
              ...response?.cp_count_data,
            };

            this.formatScheduleData('patrol', response?.data, true);
          }
        } else {
          this.toasterService.setMessage({
            errorMessage: response?.message,
            successMessage: '',
          });
        }
      });
  }

  formatScheduleData(_type = 'job', shiftData: any, restart = false) {
    if (_type === 'job') {
      if (restart) {
        this.groupedJobRosterShiftData = {};
        this.pulledJobCount = 0;
      }
      this.pulledJobCount += shiftData?.length;
      if (this.pulledJobCount < this.totalJobCount) {
        this.getNextSetOfJobs();
      } else {
        this.jobFinalApiCall = true;
      }
      const jobShiftBackup = JSON.parse(
        JSON.stringify(this.groupedJobRosterShiftData || {}),
      );
      shiftData?.forEach((job: any) => {
        if (job?.event_date in jobShiftBackup) {
          const isJobPresent = jobShiftBackup?.[job?.event_date]?.some(
            (aJob: any) => aJob?.id === job?.id,
          );
          if (!isJobPresent) jobShiftBackup[job?.event_date].push(job);
        } else {
          jobShiftBackup[job?.event_date] = [job];
        }
      });
      this.groupedJobRosterShiftData = JSON.parse(
        JSON.stringify(jobShiftBackup),
      );
    } else {
      if (restart) {
        this.groupedPatrolJobRosterShiftData = {};
        this.pulledPatrolJobCount = 0;
      }
      this.pulledPatrolJobCount += shiftData?.length;

      if (this.pulledPatrolJobCount < this.totalPatrolJobCount) {
        this.getNextSetOfPatrolJobs();
      } else {
        this.patrolJobFinalApiCall = true;
      }
      const patrolShiftBackup = JSON.parse(
        JSON.stringify(this.groupedPatrolJobRosterShiftData || {}),
      );

      shiftData?.forEach((job: any) => {
        if (!(job?.id in this.patrolCheckPointCountDictData)) return;
        job.checkpoint_count = this.patrolCheckPointCountDictData[job?.id];
        if (job?.event_date in patrolShiftBackup) {
          const isJobPresent = patrolShiftBackup?.[job?.event_date]?.some(
            (aJob: any) => aJob?.id === job?.id,
          );
          if (!isJobPresent) patrolShiftBackup[job?.event_date].push(job);
        } else {
          patrolShiftBackup[job?.event_date] = [job];
        }
      });
      this.groupedPatrolJobRosterShiftData = JSON.parse(
        JSON.stringify(patrolShiftBackup),
      );
    }

    this.sortAndFilterJobShiftData();

    this.spinnerService.hide();
  }

  getWeatherInfo() {
    const body = {
      subscriber_address: 1,
    };
    this.jobService.getWeatherInfo(body).subscribe((response: any) => {
      if (response?.status === 'success') {
        this.weatherData = response?.data?.forecast_weather;
        this.weatherAddressInfo = response?.address_data;
      }
    });
  }
  openWeatherTemplate(
    template: TemplateRef<any>,
    weatherData: any,
    selectedDate: any,
  ) {
    const dayWeather =
      weatherData?.[formatDate(new Date(selectedDate), 'yyyy-MM-dd', 'en_US')];

    this.dialogService.open(
      template,
      {
        data: {
          weatherData: dayWeather,
          selectedDate: selectedDate,
          weatherKeys: [
            ...Object.keys(dayWeather)?.filter(
              (key) => !['Condition', 'Description'].includes(key),
            ),
          ],
        },
      },
      this.viewContainerRef,
    );
  }
  changeShiftGroupBy(groupByTo: string) {
    if (groupByTo === this.shiftDataGroupBy) return;
    this.shiftDataGroupBy = groupByTo;
    localStorage.setItem(btoa('rosterGroupby'), btoa(groupByTo));
    if (this.expandAllGroups) {
      this.sortAndFilterJobShiftData();
    } else {
      this.groupedDataList = [];
      this.selectedGroupInfo = null;
      this.getRosterShifts();
    }
  }

  searchAssignees(event?: any) {
    const searchStr = event?.target?.value || '';
    this.jobService
      .getAssignees({ full_name: searchStr })
      .subscribe((response: any) => {
        if (response['status'] == 'success') {
          this.assigneeListData = response?.data;
        }
      });
  }

  openTemplate(template: TemplateRef<any>, data?: any, action?: string) {
    this.shiftRecurranceSelection = 1;
    switch (action) {
      case 'modifyAssignee':
        this.searchAssignees();
        break;
      case 'modifyConflictAssignee':
        data = { ...data, conflictJob: true };
        this.searchAssignees();
        break;
    }
    this.dialogService.open(template, { data: data }, this.viewContainerRef);
  }
  deleteJobShifts(jobData: any) {
    let reqBody: any = {};
    let reqParams: any = {};
    this.spinnerService.show('Removing Jobs');
    if (jobData?.job_type_dict?.type_identity === 4) {
      //patrol jobs
      reqBody = { ...reqBody };
      reqParams = {
        ...reqParams,
        patrol_job: 1,
        delete_job: 1,
        future_jobs_also: this.shiftRecurranceSelection == 2 ? 1 : 0,
      };
    } else {
      reqBody = { ...reqBody };
      reqParams = {
        ...reqParams,
        delete_job: 1,
        future_jobs_also: this.shiftRecurranceSelection == 2 ? 1 : 0,
      };
    }
    this.rsService
      .updateRosterShifts(jobData?.id, reqBody, reqParams)
      .then((response: any) => {
        if (response?.status === 'success') {
          setTimeout(() => {
            if (jobData?.job_type_dict?.type_identity === 4) {
              this.getRosterPatrolJobs();
            } else {
              this.getRosterStaticJobs();
            }
          }, 1000);
        } else {
          this.toasterService.setMessage({
            errorMessage: response?.message,
            successMessage: '',
          });
        }
      });
  }

  onAddShiftClick(date: any) {
    if (this.isAdmin || this.isDispatchUser) {
      this.router.navigate(['/add-job'], { fragment: 'roster' });
      sessionStorage.setItem('redirectDate', date || new Date());
      sessionStorage.setItem('selectedRosterDate', date || new Date());
    }
  }
  openEventDetailPage(job: any, selectedDate: any, template: any) {
    if (job?.shift_info?.repeat_type === 'once') {
      if (job?.job_type_dict?.type_identity === 4) {
        this.routeToPatrolJobPage(job);
      } else {
        this.routeToJobPage(job);
      }
    } else {
      const isFutureJob = trashIconIf(
        job?.shift_info,
        selectedDate,
        this.isAdmin || this.isDispatchUser,
      );
      if (isFutureJob) {
        this.openTemplate(template, { ...job, selectedDate: selectedDate });
      } else {
        if (job?.job_type_dict?.type_identity === 4) {
          this.routeToPatrolJobPage(job);
        } else {
          this.routeToJobPage(job);
        }
      }
    }
  }
  routeToJobPage(job: any) {
    this.router.navigate([`/job-detail/JOBID`], {
      queryParams: { jId: job?.id },
    });
  }
  routeToPatrolJobPage(job: any) {
    this.router.navigate([
      '/view-route',
      {
        rKey: String(job?.id),
      },
    ]);
  }

  getDateTimeForSorting(time: any) {
    let [startHour, startMin] = time?.split(':')?.map(Number);
    const today = new Date();
    today.setHours(startHour, startMin, 0, 0);
    return today.getTime();
  }
  getMobileRosterSchedulesForDate(date: any) {
    this.selectedSearchDate = getFormattedDate(date);
    this.router.navigate(['/roster'], {
      fragment: this.selectedSearchDate,
      queryParams: {},
      replaceUrl: true, // Replaces the current URL without adding a new history entry
    });
    this.getRosterShifts();
  }
  animateTransition() {
    this.isAnimating = true;
    setTimeout(() => {
      this.isAnimating = false;
    }, 300);
  }

  onTouchStart(event: TouchEvent) {
    this.startX = event.touches[0].clientX;
  }

  openEditScheduleTemplate(jobData: any) {
    const schedulerForm: UntypedFormGroup = new UntypedFormGroup({
      end_time: new FormControl(null),
      start_time: new FormControl(null),
      start_day: new FormControl(new Date()),
      end_day: new FormControl(null, Validators.required),
      repeat_times: new UntypedFormControl(null),
      repeat_type: new UntypedFormControl(null, Validators.required),
      repeat_details: new UntypedFormControl([]),
      repeat_interval: new UntypedFormControl(1, [
        Validators.required,
        Validators.pattern('^[0-9]*$'),
      ]),
      position: new UntypedFormControl(null, []),
      week_day: new UntypedFormControl(null, []),
      month_repeat_case: new UntypedFormControl(null, []),
    });

    schedulerForm.patchValue(jobData?.shift_info);
    schedulerForm.controls['start_day'].setValue(
      getFormattedDate(
        new Date(jobData?.selectedDate || this.selectedSearchDate),
      ),
    );

    const dialogRef = this.dialogService.open(SchedulerComponent, {
      data: { schedulerForm: schedulerForm, editSchedule: true },
    });
    dialogRef.afterClosed().subscribe((value: any) => {
      if (value) {
        const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
          data: {
            title: 'Are you sure?',
          },
        });
        dialogRef.afterClosed().subscribe((dialogResponse) => {
          if (dialogResponse === true) {
            let reqBody: any = { ...value };
            let reqParams: any = {};
            this.spinnerService.show('Updating Shifts');
            if (jobData?.job_type_dict?.type_identity === 4) {
              //patrol jobs
              reqBody = { ...reqBody };
              reqParams = {
                ...reqParams,
                patrol_job: 1,
                update_shift_schedule: 1,
              };
            } else {
              reqBody = { ...reqBody };
              reqParams = {
                ...reqParams,
                update_shift_schedule: 1,
              };
            }
            this.rsService
              .updateRosterShifts(jobData?.id, reqBody, reqParams)
              .then((response: any) => {
                if (response?.status === 'success') {
                  setTimeout(() => {
                    if (jobData?.job_type_dict?.type_identity === 4) {
                      this.getRosterPatrolJobs();
                    } else {
                      this.getRosterStaticJobs();
                    }
                  }, 1000);
                } else {
                  this.toasterService.setMessage({
                    errorMessage: response?.message,
                    successMessage: '',
                  });
                }
              });
          }
        });
      }
    });
  }
  duplicateShifts(jobData: any) {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      data: {
        title: 'Are you sure?',
        message: 'Confirm duplicating the shift with the same job details',
      },
    });
    dialogRef.afterClosed().subscribe((value) => {
      if (value === true) {
        sessionStorage.setItem(
          btoa('rosterCopyJobInfo'),
          JSON.stringify(jobData),
        );
        this.router.navigate(['/add-job'], { fragment: 'roster' });
      }
    });
  }
  changeAssignee(jobData: any) {
    let reqBody: any = { new_assignee_id: this.selectedAssignee?.id };
    let reqParams: any = {};
    this.spinnerService.show('Changing Assignee');
    if (jobData?.job_type_dict?.type_identity === 4) {
      //patrol jobs
      reqBody = { ...reqBody };
      reqParams = {
        ...reqParams,
        patrol_job: 1,
        change_assignee: 1,
        individual_job:
          jobData?.conflictJob || this.shiftRecurranceSelection == 1 ? 1 : 0,
      };
    } else {
      reqBody = { ...reqBody };
      reqParams = {
        ...reqParams,
        change_assignee: 1,
        individual_job:
          jobData?.conflictJob || this.shiftRecurranceSelection == 1 ? 1 : 0,
      };
    }
    this.rsService
      .updateRosterShifts(jobData?.id, reqBody, reqParams)
      .then((response: any) => {
        if (response?.status === 'success') {
          this.selectedAssignee = {};
          setTimeout(() => {
            if (jobData?.job_type_dict?.type_identity === 4) {
              this.getRosterPatrolJobs();
            } else {
              this.getRosterStaticJobs();
            }
          }, 1000);
        } else {
          this.toasterService.setMessage({
            errorMessage: response?.message,
            successMessage: '',
          });
        }
      });
  }
  searchClients(event?: any) {
    if (event?.target?.value?.length < 1) return;
    const searchStr = event?.target?.value;
    this.clientService.searchClients(searchStr).subscribe((res: any) => {
      if (res['status'] == 'success') {
        this.clientDropDownList = res?.data;
        if (!searchStr) {
          this.initialDataList.client = JSON.parse(
            JSON.stringify(this.clientDropDownList),
          );
        }
      }
    });
  }
  searchSites(event?: any) {
    if (event?.target?.value?.length < 1) return;
    const searchStr = event?.target?.value;

    this.clientService
      .searchSites({
        search_str: searchStr,
      })
      .subscribe((res: any) => {
        if (res['status'] == 'success') {
          this.siteDropDownList = res?.data;
          if (!searchStr) {
            this.initialDataList.site = JSON.parse(
              JSON.stringify(this.siteDropDownList),
            );
          }
        }
      });
  }
  searchAssignee(event?: any) {
    if (event?.target?.value?.length < 1) return;
    let params: any = { full_name: event.target.value };
    this.jobService.getAssignees(params).subscribe((response: any) => {
      if (response['status'] == 'success') {
        this.assigneeDropDownList = response?.data;
      } else {
        this.toasterService.setMessage({
          successMessage: '',
          errorMessage: response['message'],
        });
      }
    });
  }
  inputFocused(field: string) {
    this.updateInitialDataList(field);
  }
  shiftFocus(elementId: string) {
    setTimeout(() => {
      var element = <HTMLInputElement>document.getElementById(elementId);
      element?.focus();
    }, 200);
  }
  editField(field: string) {
    if (field == 'site') {
      this.shiftFocus('siteLookupId');
    }
    if (field == 'client') {
      this.shiftFocus('clientLookupId');
    }
    if (field == 'jType') {
      this.shiftFocus('jTypeLookupField');
    }

    this.updateInitialDataList(field);
  }
  updateInitialDataList(field: any) {
    this.siteDropDownList = [];
    this.clientDropDownList = [];
    setTimeout(() => {
      if (field === 'site') {
        this.siteDropDownList = JSON.parse(
          JSON.stringify(this.initialDataList?.[field] || []),
        );
      } else if (field === 'client') {
        this.clientDropDownList = JSON.parse(
          JSON.stringify(this.initialDataList?.[field] || []),
        );
      }
    }, 200);
  }
  getStartAndEndDateTime(job: any) {
    let startDateTime = new Date();
    let endDateTime = new Date();
    if (job?.shift_info?.repeat_type == 'once') {
      startDateTime = new Date(
        job?.shift_info?.start_day + ' ' + job?.shift_info?.start_time,
      );
      endDateTime = new Date(
        job?.shift_info?.end_day + ' ' + job?.shift_info?.end_time,
      );
    } else {
      startDateTime = new Date(
        job?.event_date + ' ' + job?.shift_info?.start_time,
      );
      endDateTime = new Date(job?.event_date + ' ' + job?.shift_info?.end_time);
      if (job?.shift_info?.end_time < job?.shift_info?.start_time) {
        // overnight schedule
        endDateTime.setDate(endDateTime.getDate() + 1);
      }
    }

    return [startDateTime.getTime(), endDateTime.getTime()];
  }
  shouldIncludeShift(shift: any): boolean {
    let filteredValue = true;

    if (this.appliedFilters?.client?.id) {
      filteredValue =
        shift?.company_uuid == this.appliedFilters?.client?.id && filteredValue;
    }
    if (this.appliedFilters?.site?.id) {
      filteredValue =
        shift?.site_uuid == this.appliedFilters?.site?.id && filteredValue;
    }
    if (this.appliedFilters?.assignee?.id) {
      const firstAssignee = shift?.assignee_list?.[0];
      shift.assignee_id = firstAssignee?.id || 'unassigned';
      filteredValue =
        shift?.assignee_id == this.appliedFilters?.assignee?.id &&
        filteredValue;
    }
    if (this.appliedFilters?.time?.id) {
      filteredValue =
        shift?.time_id == this.appliedFilters?.time?.id && filteredValue;
    }
    if (this.appliedFilters?.jType?.id) {
      if (this.appliedFilters?.jType?.type_identity === 4) {
        filteredValue =
          shift?.job_type_dict?.type_identity ==
            this.appliedFilters?.jType?.type_identity && filteredValue;
        // checkpoint patrol
      } else {
        filteredValue =
          shift?.job_type_dict?.id == this.appliedFilters?.jType?.id &&
          filteredValue;
      }
    }
    if (this.appliedFilters?.shiftStatus) {
      switch (this.appliedFilters?.shiftStatus) {
        case 3:
          // un assigned
          filteredValue = filteredValue && !shift?.assignee_list?.length;
          break;
        case 2:
          // jobs that users have not accepted/declined yet
          filteredValue =
            filteredValue &&
            shift?.assignee_list?.some(
              (assignee: any) => assignee?.acceptance_status == 2,
            );
          break;
        case 1:
          // job assigned and not pending
          filteredValue =
            filteredValue &&
            shift?.assignee_list?.length &&
            !shift?.assignee_list?.some(
              (assignee: any) => assignee?.acceptance_status == 2,
            );

          break;
      }
    }
    return filteredValue;
  }
  filterRosterShifts() {
    if (this.expandAllGroups) {
      this.sortAndFilterJobShiftData();
    } else {
      this.groupedDataList = [];
      this.selectedGroupInfo = null;
      this.getRosterShifts();
    }
  }
  sortAndFilterJobShiftData() {
    function* shiftIterator(
      shifts: any[],
      shouldIncludeShift: (s: any) => boolean,
    ) {
      for (const shift of shifts) {
        if (shouldIncludeShift(shift)) {
          yield shift;
        }
      }
    }
    const avoidJobStatus = new Set([2, 3, 1, 7, 9, 10, 11, 12, 13]);

    this.fullGroupedRosterData = {};
    this.groupCountsByDate = {};

    this.groupByIds = [];
    this.shiftsToPublish = [];
    let patrolRouteIds = [];

    const fullDates: string[] = [
      ...new Set([
        ...Object.keys(this.groupedJobRosterShiftData || {}),
        ...Object.keys(this.groupedPatrolJobRosterShiftData || {}),
      ]),
    ];

    const sortKey: any =
      this.shiftDataGroupBy === 'time'
        ? 'time_id'
        : this.shiftDataGroupBy === 'company'
          ? 'company_uuid'
          : this.shiftDataGroupBy === 'site'
            ? 'site_uuid'
            : this.shiftDataGroupBy === 'assignee'
              ? 'assignee_id'
              : null;

    for (const dateKey of fullDates) {
      const groupCounts: any = {};
      const shifts: any = [
        ...(this.groupedJobRosterShiftData?.[dateKey] || []),
        ...(this.groupedPatrolJobRosterShiftData?.[dateKey] || []),
      ];

      const filteredShifts: any = [];

      for (const shift of shiftIterator(
        shifts,
        this.shouldIncludeShift.bind(this),
      )) {
        if (!this.shouldIncludeShift(shift)) continue;

        if (shift?.job_type_dict?.type_identity === 4) {
          const date = new Date(dateKey);

          const oneDayFromNow = new Date();
          oneDayFromNow.setDate(oneDayFromNow.getDate() + 1);
          if (date.getTime() < oneDayFromNow.getTime()) {
            patrolRouteIds.push(shift?.id);
          }
        }

        const firstAssignee = shift?.assignee_list?.[0];
        shift.assignee_id = firstAssignee?.id || 'unassigned';
        shift.assignee_name = firstAssignee?.user_name || 'Unassigned';
        shift.assignee_email = firstAssignee?.email;
        const hour = parseInt(shift?.shift_info?.start_time?.split(':')[0], 10);
        shift.time_id = `${hour}:00`;

        filteredShifts.push(shift);

        const lastWeekDate = this.shiftWeekDays[this.shiftWeekDays?.length - 1];
        // no need to check publish shifts for past weeks
        if (
          lastWeekDate &&
          new Date(lastWeekDate).getTime() > new Date().getTime()
        ) {
          shift.requires_acceptance =
            shift?.assignee_list?.some(
              (assignee: any) =>
                assignee?.id === this.subscriberUserId &&
                assignee?.acceptance_status == 2,
            ) &&
            shift?.job_type_dict?.requires_acceptance &&
            !avoidJobStatus.has(shift?.status_dict?.status_identity);

          const shiftToPublish =
            shift?.assignee_list?.some(
              (assignee: any) => assignee?.acceptance_status == 2,
            ) &&
            shift?.job_type_dict?.requires_acceptance &&
            !avoidJobStatus.has(shift?.status_dict?.status_identity);

          if (shiftToPublish) {
            this.shiftsToPublish.push(shift);
          }
        }

        const key =
          shift?.[sortKey] ??
          (this.shiftDataGroupBy === 'assignee'
            ? 'unassigned'
            : 'checkpointPatrol');

        groupCounts[key] = (groupCounts[key] || 0) + 1;

        this.groupByIds.push(key);
      }

      filteredShifts.sort((a: any, b: any) => {
        if (this.largeView && sortKey) {
          const aKey = a?.[sortKey];
          const bKey = b?.[sortKey];

          const aHasKey = !!aKey;
          const bHasKey = !!bKey;

          if (!aHasKey && bHasKey) return 1;
          if (aHasKey && !bHasKey) return -1;
          if (!aHasKey && !bHasKey) {
            // Sort by time only
            return (
              this.getDateTimeForSorting(a?.shift_info?.start_time) -
              this.getDateTimeForSorting(b?.shift_info?.start_time)
            );
          }

          // Both have keys — compare them
          if (aKey < bKey) return -1;
          if (aKey > bKey) return 1;
        }

        // Same group key or no key — sort by start time
        return (
          this.getDateTimeForSorting(a?.shift_info?.start_time) -
          this.getDateTimeForSorting(b?.shift_info?.start_time)
        );
      });

      this.groupCountsByDate[dateKey] = groupCounts;

      this.fullGroupedRosterData[dateKey] = JSON.parse(
        JSON.stringify(filteredShifts),
      );
    }
    this.fullGroupedRosterBackupData = JSON.parse(
      JSON.stringify(this.fullGroupedRosterData),
    );
    const filtered = Object.fromEntries(
      Object.entries(this.appliedFilters).filter(
        ([_, value]) =>
          (value as any)?.id !== undefined || [1, 2, 3].includes(value as any),
      ),
    );

    if (this.shiftDataGroupBy === 'time') {
      this.groupByIds = Array.from(new Set(this.groupByIds));
      this.groupByIds?.sort((a: any, b: any) => {
        const dateA = new Date(`1970-01-01T${a}:00`);
        const dateB = new Date(`1970-01-01T${b}:00`);
        return dateA.getTime() - dateB.getTime();
      });
    } else {
      this.groupByIds = this.moveStringToEndUnique(
        this.groupByIds,
        this.shiftDataGroupBy === 'assignee'
          ? 'unassigned'
          : 'checkpointPatrol',
      );
    }

    this.appliedFilters = { ...filtered };

    if (this.isAdmin || this.isDispatchUser) {
      this.findConflicts();

      if (this.patrolJobFinalApiCall && this.jobFinalApiCall) {
        this.getPublishedHistory();
        patrolRouteIds = [...new Set(patrolRouteIds)];

        if (patrolRouteIds?.length) {
          this.getPatrolMissedData(patrolRouteIds);
        }
      }
    }
  }
  moveStringToEndUnique(arr: string[], str: string): string[] {
    const unique = Array.from(new Set(arr));
    const filtered = unique.filter((item) => item !== str);
    filtered.push(str);
    return filtered;
  }

  findConflicts() {
    this.conflictData = {};
    const avoidJobStatus = new Set([2, 3, 1, 7, 9, 10, 11, 12, 13]);

    // Flatten jobs
    const allJobs = [...Object.values(this.fullGroupedRosterBackupData).flat()];

    // Normalize today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTime = today.getTime();

    // Pre-filter valid jobs
    const validJobs = allJobs
      .map((job: any) => {
        const [start, end] = this.getStartAndEndDateTime(job);
        return { ...job, start, end };
      })
      .filter((job) => {
        if (avoidJobStatus.has(job?.status_dict?.status_identity)) return false;
        if (job?.end < todayTime) return false;
        return true;
      });

    // Group by assignee
    const jobsByAssignee: Record<string, any[]> = {};
    for (const job of validJobs) {
      for (const assignee of job.assignee_list || []) {
        const aid = assignee?.id;
        if (!jobsByAssignee[aid]) jobsByAssignee[aid] = [];
        jobsByAssignee[aid].push(job);
      }
    }

    // Detect conflicts per assignee
    for (const [aid, jobList] of Object.entries(jobsByAssignee)) {
      // Sort by start time
      const sorted = jobList.sort((a, b) => a?.start - b?.start);

      const clusters: any[][] = [];
      let currentCluster: any[] = [];

      for (const job of sorted) {
        if (
          currentCluster?.length === 0 ||
          job?.start < currentCluster[currentCluster?.length - 1]?.end
        ) {
          // Overlaps with last job in cluster
          currentCluster.push(job);
          // Extend cluster end if needed
          currentCluster[currentCluster?.length - 1].end = Math.max(
            currentCluster[currentCluster?.length - 1]?.end,
            job.end,
          );
        } else {
          // Push finished cluster
          if (currentCluster?.length > 1) clusters.push(currentCluster);
          currentCluster = [job];
        }
      }
      if (currentCluster?.length > 1) clusters.push(currentCluster);

      // Save conflicts
      for (const cluster of clusters) {
        const eventDateKey = formatDate(
          new Date(cluster[0].start),
          'yyyy-MM-dd',
          'en_US',
        );
        if (!this.conflictData[eventDateKey])
          this.conflictData[eventDateKey] = {};
        this.conflictData[eventDateKey][aid] = {
          jobs: cluster,
          assigneeInfo: cluster[0].assignee_list.find(
            (a: any) => a?.id === aid,
          ),
        };
      }
    }
  }
  onJobTypeChange(event: any) {
    const jType = this.jobTypesDataList?.find(
      (jType: any) => jType?.id === event.target.value,
    );
    if (jType?.id) {
      this.appliedFilters.jType = {
        id: jType?.id,
        name: jType?.name,
        type_identity: jType?.type_identity,
      };
    }
  }
  clearAndFocusTextBox(elementId: string) {
    setTimeout(() => {
      var element = <HTMLInputElement>document.getElementById(elementId);
      element?.focus();
    }, 100);
  }
  onJobActionClick(action: string, jobData: any) {
    if (jobData?.job_type_dict?.type_identity === 4) {
      let updateData: any = { patrol_route_id: jobData?.id };
      let reqParams: any = {};
      switch (action) {
        case 'decline':
          reqParams.decline_job = 1;
          updateData.decline_reason = this.declineReason;
          break;
        case 'accept':
          reqParams.accept_job = 1;
      }
      this.rsService
        .managePatrolRoute(updateData, reqParams)
        .then((response: any) => {
          if (response?.status === 'success') {
            this.declineReason = '';
            this.spinnerService.hide();
            this.getRosterPatrolJobs();
          } else {
            this.spinnerService.hide();
            this.toasterService.setMessage({
              successMessage: '',
              errorMessage: response?.message,
            });
          }
        });
    } else {
      let requestData: any = {};
      requestData['method'] = 'put';
      requestData['url'] = 'api/job/' + jobData?.id + '/';
      requestData['id'] = jobData?.id;
      switch (action) {
        case 'accept':
          requestData['data'] = {
            accept_job: this.subscriberUserId,
          };
          break;
        case 'decline':
          requestData['data'] = {
            decline_job: this.subscriberUserId,
            decline_reason: this.declineReason,
          };

          break;
      }

      this.jobService.updateJob(requestData).then((response: any) => {
        if (response['status'] == 'success') {
          this.declineReason = '';
          this.spinnerService.hide();
          this.getRosterStaticJobs();
        } else {
          this.spinnerService.hide();
          this.toasterService.setMessage({
            errorMessage: response['message'],
          });
        }
      });
    }
  }

  async sendShiftEmailAlert(template: any) {
    const assigneeDict: any = {};
    this.shiftsToPublishListFiltered?.forEach((shift: any) => {
      if (shift?.assignee_id) {
        if (!(shift?.assignee_id in assigneeDict)) {
          assigneeDict[shift?.assignee_id] = {
            count: 0,
            assignee_id: shift?.assignee_id,
            assignee_name: shift?.assignee_name,
            assignee_email: shift?.assignee_email,
          };
        }
        assigneeDict[shift?.assignee_id]['count'] += 1;
      }
    });
    const dialogRef = this.dialogService.open(
      template,
      {
        data: Object.values(assigneeDict),
      },
      this.viewContainerRef,
    );
    dialogRef.afterClosed().subscribe((value) => {
      if (value === true) {
        let reqBody: any = {
          job_ids: [
            ...new Set(
              this.shiftsToPublishListFiltered?.map((job: any) => job?.id),
            ),
          ],
          event_dates: [
            ...new Set(
              this.shiftWeekDays?.map((date: any) => new Date(date).getTime()),
            ),
          ],
        };

        this.rsService.publishRosterData(reqBody).subscribe((response: any) => {
          if (response?.status === 'success') {
            this.spinnerService.hide();
            this.shiftsToPublish = [];
            this.shiftsToPublishListFiltered = [];

            this.toasterService.setMessage({
              errorMessage: '',
              successMessage: 'Published',
            });
          } else {
            this.spinnerService.hide();
            this.toasterService.setMessage({
              errorMessage: response?.message,
              successMessage: '',
            });
          }
        });
      }
    });
  }
  getPatrolMissedData(patrolRouteIds: any) {
    this.checkPointService
      .missedCheckpoints(
        { patrol_ids: patrolRouteIds },
        { missed_patrol_scans: 1 },
      )
      .subscribe((response: any) => {
        if (response['status'] == 'success') {
          this.patrolMissedDataList = response?.data;

          this.spinnerService.hide();
        } else {
          this.spinnerService.hide();
          this.toasterService.setMessage({
            successMessage: '',
            errorMessage: response['message'],
          });
        }
      });
  }

  filterDataByGroupByField(jobData: any) {
    switch (this.shiftDataGroupBy) {
      case 'site':
        if (jobData?.job_type_dict?.type_identity === 4) {
          const jType = this.jobTypesDataList?.find(
            (jType: any) => jType?.type_identity == 4,
          );
          if (jType?.id) {
            if (this.appliedFilters?.jType?.id === jType?.id) {
              this.appliedFilters.jType = {};
            } else {
              this.appliedFilters.jType = {
                id: jType?.id,
                name: jType?.name,
                type_identity: jType?.type_identity,
              };
            }
          }
        } else {
          if (this.appliedFilters?.site?.id === jobData?.site_uuid) {
            this.appliedFilters.site = {};
          } else {
            this.appliedFilters.site = {
              id: jobData?.site_uuid,
              company_name: jobData?.site_name,
            };
          }
        }
        break;
      case 'company':
        if (jobData?.job_type_dict?.type_identity === 4) {
          const jType = this.jobTypesDataList?.find(
            (jType: any) => jType?.type_identity == 4,
          );
          if (this.appliedFilters?.jType?.id === jType?.id) {
            this.appliedFilters.jType = {};
          } else {
            this.appliedFilters.jType = {
              id: jType?.id,
              name: jType?.name,
              type_identity: jType?.type_identity,
            };
          }
        } else {
          if (this.appliedFilters?.client?.id === jobData?.company_uuid) {
            this.appliedFilters.client = {};
          } else {
            this.appliedFilters.client = {
              id: jobData?.company_uuid,
              company_name: jobData?.company_name,
            };
          }
        }
        break;
      case 'assignee':
        if (this.appliedFilters?.assignee?.id === jobData?.assignee_id) {
          this.appliedFilters.assignee = {};
        } else {
          this.appliedFilters.assignee = {
            id: jobData?.assignee_id,
            full_name: jobData?.assignee_name,
          };
        }

        break;
      case 'time':
        if (this.appliedFilters?.time?.id === jobData?.time_id) {
          this.appliedFilters.time = {};
        } else {
          this.appliedFilters.time = {
            id: jobData?.time_id,
            full_name: jobData?.time_id,
          };
        }

        break;
    }
    this.sortAndFilterJobShiftData();
  }
  getPublishedHistory() {
    const lastWeekDate = this.shiftWeekDays[this.shiftWeekDays?.length - 1];

    // no need to check for  publish shifts histiory for past weeks

    if (
      lastWeekDate &&
      new Date(lastWeekDate).getTime() > new Date().getTime()
    ) {
      let reqBody: any = {
        event_dates: [
          ...new Set(
            this.shiftWeekDays?.map((date: any) => new Date(date).getTime()),
          ),
        ],
      };
      this.rsService
        .publishRosterData(reqBody, { get_published_history: 1 })
        .subscribe((response: any) => {
          if (response?.status === 'success') {
            this.publishedHistoryData = response?.data || {};
            this.checkAndFilterShiftsToPublish();
          } else {
            this.spinnerService.hide();
            this.toasterService.setMessage({
              errorMessage: response?.message,
              successMessage: '',
            });
          }
        });
    }
  }
  checkAndFilterShiftsToPublish() {
    this.shiftsToPublishListFiltered = [];
    if (!this.shiftsToPublish?.length) return;

    if (this.publishedHistoryData) {
      this.shiftsToPublish?.forEach((shift: any) => {
        const shiftAlreadySent = (
          this.publishedHistoryData?.[shift?.id] || []
        )?.includes(shift?.assignee_id);

        if (!shiftAlreadySent) this.shiftsToPublishListFiltered.push(shift);
      });
    } else {
      setTimeout(() => {
        this.checkAndFilterShiftsToPublish();
      }, 1500);
    }
  }
  resetvariables() {
    this.fullGroupedRosterBackupData = {};
    this.fullGroupedRosterData = {};
    this.groupedJobRosterShiftData = {};
    this.totalJobCount = 0;
    this.pageSizeJobs = 30;
    this.previousJobs = 0;
    this.pulledJobCount = 0;

    this.groupedPatrolJobRosterShiftData = {};
    this.totalPatrolJobCount = 0;
    this.pageSizePatrolJobs = 30;
    this.previousPatrolJobs = 0;
    this.pulledPatrolJobCount = 0;

    this.patrolMissedDataList = [];
    this.patrolCheckPointCountDictData = {};

    this.groupCountsByDate = {};
  }
  getGroupedDataJobList(data: any) {
    if (data?.grouped_id === this.selectedGroupInfo?.id) {
      this.selectedGroupInfo = null;
      return;
    }
    this.stopAllSubjects();
    this.selectedGroupInfo = data;

    sessionStorage.setItem(
      btoa('selectedGroupInfo'),
      JSON.stringify(this.selectedGroupInfo),
    );
    this.resetvariables();

    if (this.selectedGroupInfo?.grouped_id == 4) {
      this.getRosterPatrolJobs();
    } else if (['time', 'assignee'].includes(this.shiftDataGroupBy)) {
      this.getRosterShifts();
    } else {
      this.getRosterStaticJobs();
    }
  }
  onExpandView() {
    this.selectedGroupInfo = null;
    this.groupedDataList = [];
    this.expandAllGroups = !this.expandAllGroups;
    this.resetvariables();
    this.stopAllSubjects();
    localStorage.setItem(
      btoa('expandAllGroups'),
      btoa(String(this.expandAllGroups)),
    );
    this.getRosterShifts();
  }

  redirectToPage(string: any) {
    if (string == 'unAssignedJobs') {
      this.router.navigate(['/roster-jobs'], { fragment: 'unAssignedJobs' });
    }
  }
}
